import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { ActivityIndicator, useTheme } from 'react-native-paper';
import { CompositeScreenProps } from '@react-navigation/native';
import {
  ShouldStartLoadRequest,
  WebViewRenderProcessGoneEvent,
  WebViewTerminatedEvent,
} from 'react-native-webview/lib/WebViewTypes';
import { useQueryClient } from 'react-query';
import { StackScreenProps } from '@react-navigation/stack';
import * as Sentry from '../components/sentry/sentry';
import { screenNames } from '../navigators/screenNames';
import WebView from '../components/webview/WebView';
import { AppDrawerScreenProp } from '../components/DrawerMenuContent';
import { InformationStackRouteParams } from '../navigators/InformationStackNavigator';
import podExport from '../../pod-exports.json';
import { ThirdPartyAuthProvider } from '../types/appsync-types';
import { invalidateThirdPartyAuthData } from '../apis/appsyncApis';

export type ThirdPartyAuthWebViewRouteParams = {
  url: string;
  authProvider: ThirdPartyAuthProvider;
};

type Props = CompositeScreenProps<
  StackScreenProps<InformationStackRouteParams, 'ThirdPartyAuthWebView'>,
  AppDrawerScreenProp
>;

export default function ThirdPartyAuthWebView({ navigation, route }: Readonly<Props>) {
  const { colors } = useTheme();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [reloadKey, setReloadKey] = useState<number>(0);
  const queryClient = useQueryClient();

  const { authProvider } = route.params;

  const url = useMemo(() => {
    return route.params?.url ?? '';
  }, [route.params?.url]);
  const webview = useRef<WebView>(null);

  const onShouldStartLoad = useCallback(
    (ev: ShouldStartLoadRequest) => {
      const newUrl = new URL(ev.url);
      if (newUrl.href.startsWith(podExport.webLinkBaseUrl) || newUrl.href.startsWith('http://localhost')) {
        const status = (newUrl.searchParams.get('status') as 'error' | 'success') ?? undefined;
        const reason = newUrl.searchParams.get('reason') ?? undefined;
        webview.current?.stopLoading();
        invalidateThirdPartyAuthData(queryClient);
        // popTo would be ideal, but that is added in react-navigation v7 so going with this for now
        navigation.pop(2);
        navigation.navigate(screenNames.ThirdPartyAuthDetail, {
          authProvider,
          status,
          reason,
        });
        return false;
      }
      return true;
    },
    [queryClient, navigation, authProvider],
  );

  const renderLoading = useCallback(() => {
    return (
      <View style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator animating={true} color={colors.secondary} />
        </View>
      </View>
    );
  }, [colors]);

  const handleLoadComplete = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleRenderProcessGone = useCallback((evt: WebViewRenderProcessGoneEvent) => {
    Sentry.captureException(new Error('WebViewRenderProcessGoneEvent: ' + evt.toString()));
    setReloadKey((prev) => prev + 1);
    setIsLoaded(false);
  }, []);
  const handleContentProcessDidTerminate = useCallback((evt: WebViewTerminatedEvent) => {
    Sentry.captureException(new Error('WebViewTerminatedEvent: ' + evt.toString()));
    setReloadKey((prev) => prev + 1);
    setIsLoaded(false);
  }, []);

  return (
    <View style={{ flex: 1, backgroundColor: colors.background }}>
      <WebView
        style={{ opacity: isLoaded ? 1 : 0 }}
        source={{ uri: url }}
        bounces={false}
        key={reloadKey}
        onShouldStartLoadWithRequest={onShouldStartLoad}
        ref={webview}
        onLoadEnd={handleLoadComplete}
        onRenderProcessGone={handleRenderProcessGone}
        onContentProcessDidTerminate={handleContentProcessDidTerminate}
      />
      {isLoaded ? null : renderLoading()}
    </View>
  );
}
